/* Selection only. All cart writes belong to OmjCart. */
(function () {
  'use strict';

  function init(root) {
    if (root.getCartItems) return;

    /*
      Each slot stores both the selected variant id and its real price in cents.
      This lets the visible bundle price follow the exact Jellys selected by the
      customer instead of assuming that every pot costs the same as the product
      page currently being viewed.

      Discount display logic mirrors the current Kaching / Shopify behaviour:
      for a X paid + Y free offer, the Y least expensive selected Jellys are
      treated as free. Kaching / Shopify remains the source of truth for the
      actual cart discount; this script only keeps the buy-box estimate aligned.
    */
    const selections = new Map();
    const modal = root.querySelector('[data-bdl-modal]');
    let editing = null;
    let returnFocus = null;

    const currentPrice = Number(root.dataset.currentPrice || 0);
    const currency = root.dataset.currency || 'EUR';

    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.hidden = true;
    root.appendChild(status);

    /* Ligne d'économie affichée sous les offres (ex. « Tu économises 39,90 € »). */
    const savings = document.createElement('p');
    savings.className = 'bdl-savings';
    savings.hidden = true;
    root.appendChild(savings);

    const active = () => root.querySelector('[data-bdl-radio]:checked')?.value || '1';

    /* Abonnement coché sur l'offre 1 pot : { plan, price } ou null. */
    function subscription(tierNo = active()) {
      const box = root.querySelector('[data-bdl-sub]');
      if (!box || !box.checked) return null;
      /* Un plan par cure (data-sub1/2/3 = « id|pourcentage ») : abonnement possible sur les 3 offres. */
      const perTier = root.dataset['sub' + tierNo];
      if (perTier) {
        const [plan, pct] = perTier.split('|').map(Number);
        if (Number.isSafeInteger(plan) && plan > 0 && Number.isFinite(pct)) return { plan, pct };
        return null;
      }
      const plan = Number(box.value);
      const price = Number(box.dataset.subPrice);
      if (!Number.isSafeInteger(plan) || plan <= 0 || !Number.isFinite(price)) return null;
      return { plan, price };
    }

    /* Plan d'abonnement propre à chaque masque (un plan par prix) : lu sur le bouton du masque dans le choix des Jellys. */
    const PLAN_NAMES = { '1': 'Tous les 30 jours', '2': 'Tous les 90 jours', '3': 'Tous les 180 jours' };
    function planFor(id, tierNo, fallback) {
      const btn = root.querySelector('[data-bdl-modal-pick][data-variant-id="' + id + '"]');
      const raw = btn?.dataset.plans || '';
      const want = PLAN_NAMES[tierNo];
      for (const part of raw.split(';')) {
        const eq = part.lastIndexOf('=');
        if (eq < 0 || part.slice(0, eq) !== want) continue;
        const [plan, pct] = part.slice(eq + 1).split('|').map(Number);
        if (Number.isSafeInteger(plan) && plan > 0 && Number.isFinite(pct)) return { plan, pct };
      }
      return fallback;
    }

    function formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'fr-FR',
          {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }
        ).format(amount);
      } catch (_) {
        return amount.toFixed(2).replace('.', ',') + ' €';
      }
    }

    function getTierPrices(tier) {
      if (!tier) return null;

      const tierNumber = tier.dataset.tier || '1';
      const freeCount = Math.max(0, Number(tier.dataset.free || 0));

      if (tierNumber === '1') {
        /* Prix abonné seulement quand la cure 1 mois est choisie : sinon elle réaffiche son prix normal. */
        /* Avec un plan par cure, le prix abonné reste affiché sur toutes les cures tant que la case est cochée. */
        const sub = root.dataset.sub1 ? subscription('1') : (active() === '1' ? subscription() : null);
        const subPrice = sub && sub.pct != null ? Math.round(currentPrice * (1 - sub.pct / 100)) : sub?.price;
        return {
          current: sub ? subPrice : currentPrice,
          compare: currentPrice
        };
      }

      const slots = [...tier.querySelectorAll('[data-bdl-slot-btn]')];

      /*
        The first product is fixed: it is the product page currently open.
        Until a slot is filled, use the current product price as a neutral
        placeholder. As soon as a Jelly is selected, its real price replaces
        that placeholder and the displayed offer updates immediately.
      */
      const prices = [currentPrice];
      const ids = [null];

      slots.forEach(slot => {
        const selected = selections.get(slot);
        prices.push(selected?.price ?? currentPrice);
        ids.push(selected?.id ?? null);
      });

      const compare = prices.reduce((sum, price) => sum + price, 0);
      /* Abonnement sur cette cure : chaque pot prend la remise du plan, puis les pots offerts s'appliquent comme d'habitude. */
      const tierSub = subscription(tierNumber);
      if (tierSub && tierSub.pct != null) {
        prices.forEach((price, i) => {
          const p = ids[i] ? planFor(ids[i], tierNumber, tierSub) : tierSub;
          prices[i] = Math.round(price * (1 - p.pct / 100));
        });
      }
      const sorted = [...prices].sort((a, b) => a - b);
      const freeValue = sorted
        .slice(0, Math.min(freeCount, sorted.length))
        .reduce((sum, price) => sum + price, 0);

      const total = prices.reduce((sum, price) => sum + price, 0);
      return {
        current: Math.max(0, total - freeValue),
        compare
      };
    }

    function updateTierPrice(tier) {
      const values = getTierPrices(tier);
      if (!values) return;

      const priceWrap = tier.querySelector('[data-bdl-price]');
      if (!priceWrap) return;

      const currentNode =
        priceWrap.querySelector('[data-bdl-price-current]') || priceWrap;
      const compareNode = priceWrap.querySelector('[data-bdl-price-compare]');

      currentNode.textContent = formatMoney(values.current);

      if (compareNode) {
        compareNode.textContent = formatMoney(values.compare);
        compareNode.hidden = values.compare <= values.current;
      }

      /* Pastille « −X % » : avec l'abonnement, elle affiche la vraie remise (jamais moins que l'offre de base). */
      const pct = tier.querySelector('.bdl-pct');
      if (pct && values.compare > 0) {
        if (!pct.dataset.base) pct.dataset.base = pct.textContent;
        const base = Number((pct.dataset.base.match(/\d+/) || [0])[0]);
        const real = Math.floor((values.compare - values.current) * 100 / values.compare);
        pct.textContent = subscription(tier.dataset.tier || '1') && real > base ? '−' + real + ' %' : pct.dataset.base;
      }
    }

    function updateSavings() {
      const tier = root.querySelector('[data-bdl-tier][data-tier="' + active() + '"]');
      const values = getTierPrices(tier);
      if (!values || values.compare <= values.current) {
        savings.hidden = true;
        return;
      }
      const gift = tier.querySelector('.bdl-gift') ? ' + le kit offert' : '';
      const amount = document.createElement('strong');
      amount.textContent = formatMoney(values.compare - values.current);
      savings.replaceChildren('Tu économises ', amount, gift);
      savings.hidden = false;
    }

    function updateAllTierPrices() {
      root.querySelectorAll('[data-bdl-tier]').forEach(updateTierPrice);
      updateSavings();
    }

    function update() {
      root.querySelectorAll('[data-bdl-tier]').forEach(el => {
        el.classList.toggle('is-selected', el.dataset.tier === active());
      });

      updateAllTierPrices();
      status.hidden = true;
    }

    function close() {
      if (!modal) return;
      modal.hidden = true;
      editing = null;
      returnFocus?.focus();
    }

    root.addEventListener('change', event => {
      if (event.target.matches('[data-bdl-radio], [data-bdl-sub]')) update();
    });

    /* Le texte de l'abonnement coche aussi la case (la carte est un <label> du bouton radio). */
    root.addEventListener('click', event => {
      const row = event.target.closest('[data-bdl-sub-row]');
      if (!row || event.target.matches('[data-bdl-sub]')) return;
      event.preventDefault();
      const box = row.querySelector('[data-bdl-sub]');
      box.checked = !box.checked;
      update();
    }, true);

    root.addEventListener('click', event => {
      const button = event.target.closest(
        '[data-bdl-slot-btn], [data-bdl-modal-pick], [data-bdl-modal-close]'
      );

      if (!button || !root.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      if (button.matches('[data-bdl-modal-close]')) {
        close();
        return;
      }

      if (button.matches('[data-bdl-slot-btn]')) {
        if (!modal) return;

        editing = button;
        returnFocus = button;
        modal.hidden = false;
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-label', 'Choisis ton soin');
        modal.querySelector('button')?.focus();
        return;
      }

      if (!editing) return;

      const id = Number(button.dataset.variantId);
      const price = Number(button.dataset.price);

      if (!Number.isSafeInteger(id) || id <= 0 || button.disabled) return;
      if (!Number.isFinite(price) || price < 0) return;

      selections.set(editing, {
        id,
        price
      });

      const image = document.createElement('img');
      image.src = button.dataset.image;
      image.alt = button.dataset.title;

      editing.replaceChildren(image);
      editing.classList.remove('is-empty');
      editing.classList.add('is-filled');
      editing.setAttribute('aria-label', 'Changer : ' + button.dataset.title);
      editing.removeAttribute('aria-invalid');

      /* Update the tier before close() clears the editing reference. */
      const editedTier = editing.closest('[data-bdl-tier]');
      updateTierPrice(editedTier);
      updateSavings();

      close();
      status.hidden = true;
    });

    root.addEventListener('keydown', event => {
      if (!modal || modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
      }

      if (event.key === 'Tab') {
        const buttons = [...modal.querySelectorAll('button:not([disabled])')];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    });

    root.getCartItems = function () {
      const tier = root.querySelector(
        '[data-bdl-tier][data-tier="' + active() + '"]'
      );
      const slots = tier
        ? [...tier.querySelectorAll('[data-bdl-slot-btn]')]
        : [];
      const missing = slots.filter(slot => !selections.has(slot));

      if (missing.length) {
        missing.forEach(slot => slot.setAttribute('aria-invalid', 'true'));
        status.textContent =
          'Choisis les ' +
          missing.length +
          ' soins manquants avant d’ajouter ton offre.';
        status.hidden = false;
        missing[0].focus();
        return null;
      }

      const quantities = new Map();
      const form = [...document.querySelectorAll('form[data-product-form]')].find(
        f => f.dataset.bdlTarget === root.id
      );
      const first = Number(
        form?.querySelector('[data-variant-id]')?.value ||
          root.dataset.currentVariant
      );

      if (!Number.isSafeInteger(first) || first <= 0) return null;

      const sub = subscription();
      if (sub && active() === '1') return [{ id: first, quantity: 1, selling_plan: sub.plan }];

      quantities.set(first, 1);

      slots.forEach(slot => {
        const selected = selections.get(slot);
        const id = selected?.id;

        if (!Number.isSafeInteger(id) || id <= 0) return;
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });

      /*
        One entry per pot (quantity 1). Wi BOGO+ only adds the free kit right
        away when the add request holds several entries: with { quantity: 3 }
        of the same Jelly the kit appeared only after a page reload. Shopify
        still merges identical pots into a single cart line.
      */
      /* Pots en abonnement seulement si cette cure a son propre plan (data-subN) : les autres pages gardent l'ancien comportement. */
      const tierPlan = root.dataset['sub' + active()] ? sub : null;
      const items = [...quantities].flatMap(([id, quantity]) =>
        Array.from({ length: quantity }, () => (tierPlan ? { id, quantity: 1, selling_plan: (id === first ? tierPlan : planFor(id, active(), tierPlan)).plan } : { id, quantity: 1 }))
      );
      /* Kit d'application au choix : ajouté seulement si la case est cochée (achat unique, même avec un abonnement : 1er colis). */
      const kit = root.querySelector('[data-bdl-kit]');
      const kitId = Number(kit?.value);
      /* Le choix est mémorisé dans le panier : le panier (omj-cart.js) ajoute ou retire le kit en conséquence. */
      if (kit) window.OmjKitChoice = kit.checked ? 'oui' : 'non';
      if (kit) fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { omj_kit: kit.checked ? 'oui' : 'non' } }) }).catch(() => {});
      if (kit && kit.checked && active() !== '1' && Number.isSafeInteger(kitId) && kitId > 0) items.push({ id: kitId, quantity: 1, properties: { '_BOGO+': '39351_' + first } });
      return items;
    };

    /* Apply editor defaults once, using only currently eligible choices. */
    const defaultChoices = new Map(
      [...root.querySelectorAll('[data-bdl-modal-pick]')]
        .map(button => [button.dataset.variantId, button])
    );
    root.querySelectorAll('[data-bdl-slot-btn]').forEach(slot => {
      const choice = defaultChoices.get(slot.dataset.defaultVariantId);
      if (!choice || choice.disabled || selections.has(slot)) return;
      const id = Number(choice.dataset.variantId);
      const price = Number(choice.dataset.price);
      if (!Number.isSafeInteger(id) || id <= 0) return;
      if (!Number.isFinite(price) || price < 0) return;
      selections.set(slot, { id, price });
      const image = document.createElement('img');
      image.src = choice.dataset.image;
      image.alt = choice.dataset.title;
      slot.replaceChildren(image);
      slot.classList.remove('is-empty');
      slot.classList.add('is-filled');
      slot.setAttribute('aria-label', 'Changer : ' + choice.dataset.title);
      slot.removeAttribute('aria-invalid');
    });

    update();
  }

  function boot(container = document) {
    if (container.matches?.('[data-omj-bundle]')) init(container);
    container.querySelectorAll('[data-omj-bundle]').forEach(init);
  }

  boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
